import { AuthCustomer } from '@getjust/api-schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import { useCustomRouter } from '$hooks/client';
import { useShop } from '$hooks/queries';
import {
  useCartIdAtom,
  useEmailAtom,
  useFormMessageAtom,
  useLoginAtom,
  useTemporaryCartKeyAtom,
} from '$hooks/state';
import { CART_QUERY_KEY, USE_AUTH_LEGACY } from '$src/constants';
import { useAuth } from '$src/hooks/auth';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useShowChallengeAtom } from '$src/hooks/state/useShowChallengeAtom';
import { authAPI } from '$src/http';

const MUTATION_LOGIN_CHALLENGE_KEY = 'MUTATION/LOGIN_CHALLENGE';

export const useLoginChallenge = () => {
  const router = useCustomRouter();
  const { onEmailChange } = useEmailAtom();
  const { loginInfo, onLoginChange } = useLoginAtom();
  const { track } = useTrackAnalytics();
  const { setIsAuthenticated, setLegacyState } = useAuth();
  const { close } = useShowChallengeAtom();
  const { formMessage } = useFormMessageAtom();
  const { onCartIdChange } = useCartIdAtom();
  const { cartkey } = useTemporaryCartKeyAtom();
  const queryClient = useQueryClient();
  const { data: shop } = useShop();

  const mutation = useMutation({
    mutationFn: (code: string) =>
      authAPI.post<AuthCustomer.AuthChallengeResponseBody>('/challenge', {
        userId: loginInfo!.userId!,
        sessionId: loginInfo!.sessionId!,
        code,
        clientId: formMessage?.shopId ?? 'web',
        cartkey: cartkey || uuidv4(),
      }),
    mutationKey: [MUTATION_LOGIN_CHALLENGE_KEY],
    onError: async (error: any) => {
      track(AnalyticsEventName.OTP_FAILED);
      if (error.response.data.sessionId) {
        onLoginChange({ ...loginInfo, sessionId: error.response.data.sessionId });
      }
    },
    onSuccess: async ({ data }) => {
      track(AnalyticsEventName.OTP_SUCCEEDED);

      if (USE_AUTH_LEGACY && data.outcome === 'loggedIn') {
        setLegacyState({ accessToken: data.accessToken ?? '', refreshToken: data.refreshToken ?? '' });
      }

      if (data.outcome === 'loggedIn') {
        const preparedCartId = 'preparedCartId' in data ? data.preparedCartId : undefined;

        if (preparedCartId) {
          try {
            onCartIdChange(preparedCartId);

            // Refetch cart query immediately to load shipping methods
            // Wait for refetch to complete before navigation to ensure data is available
            if (shop?.id) {
              // Small delay to ensure cartId is propagated in all hooks before refetch
              await new Promise((resolve) => setTimeout(resolve, 100));
              await queryClient.refetchQueries({ queryKey: [CART_QUERY_KEY, preparedCartId] });
            } else {
              // If shop is not loaded, invalidate and let React Query refetch when component mounts
              queryClient.invalidateQueries({ queryKey: [CART_QUERY_KEY, preparedCartId] });
            }
          } catch (error) {
            console.warn('Failed to update cartId with preparedCartId:', error);
          }
        } else {
          console.warn('preparedCartId is not present in the response', {
            data,
            cartkey,
            hook: useLoginChallenge.name,
          });
        }
      }

      setIsAuthenticated(true);
      onEmailChange();
      close();
      router.push('/authenticated');
    },
  });

  return {
    onChallenge: mutation.mutateAsync,
    status: mutation.status,
  };
};
